import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Volleyball, MagnifyingGlass, CheckCircle, Trash, Image as ImageIcon, UploadSimple, Phone } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CourtRentalAdmin() {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [uploadingZelle, setUploadingZelle] = useState(null);
  const [sortMode, setSortMode] = useState('recent');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/court-rentals`, { withCredentials: true });
      setRentals(data || []);
    } catch (e) {
      toast.error('Erro ao carregar alugueis');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusUpdate = async (id) => {
    try {
      await axios.put(`${API}/admin/court-rentals/${id}`, { status: 'confirmada', payment_status: 'pago', payment_method: 'manual' }, { withCredentials: true });
      toast.success('Pagamento confirmado');
      fetchData();
    } catch (e) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este aluguel?')) return;
    try {
      await axios.delete(`${API}/admin/court-rentals/${id}`, { withCredentials: true });
      toast.success('Removido');
      fetchData();
    } catch (e) { toast.error('Erro'); }
  };

  const handleZelleUpload = async (id, file) => {
    if (!file) return;
    setUploadingZelle(id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/portal/court-rental/zelle-receipt?rental_id=${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true });
      toast.success('Comprovante enviado!');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    } finally {
      setUploadingZelle(null);
    }
  };

  const viewReceipt = (id) => {
    const token = localStorage.getItem('access_token');
    window.open(`${API}/admin/court-rentals/zelle-receipt/${id}?auth=${token}`, '_blank');
  };

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return rentals.filter((r) => {
      if (statusFilter !== 'all' && (r.payment_status || 'pendente') !== statusFilter) return false;
      if (sourceFilter !== 'all' && r.source !== sourceFilter) return false;
      if (!term) return true;
      return (r.name || '').toLowerCase().includes(term) || (r.email || '').toLowerCase().includes(term) || (r.phone || '').includes(search) || (r.date || '').includes(search);
    });
  }, [rentals, search, statusFilter, sourceFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      if (sortMode === 'name-asc') return (a.name || '').localeCompare(b.name || '', 'pt-BR');
      if (sortMode === 'name-desc') return (b.name || '').localeCompare(a.name || '', 'pt-BR');
      return (b.created_at || '').localeCompare(a.created_at || '');
    });
    return arr;
  }, [filtered, sortMode]);

  const total = rentals.length;
  const paid = rentals.filter((r) => r.payment_status === 'pago').length;
  const pending = rentals.filter((r) => (r.payment_status || 'pendente') === 'pendente').length;
  const revenue = rentals.filter((r) => r.payment_status === 'pago').reduce((s, r) => s + (Number(r.amount) || 0), 0);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="court-rental-admin-page">
        <div>
          <h1 className="page-title flex items-center gap-3">
            <Volleyball size={28} className="text-[#0077B6]" weight="fill" /> Aluguel de Quadra
          </h1>
          <p className="text-[#73706A] mt-1">$40 alunos &middot; $50 não alunos</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <button onClick={() => setStatusFilter('all')} className={`stat-card text-left transition-all ${statusFilter === 'all' ? 'ring-2 ring-[#E85D04]' : ''}`}>
            <p className="text-sm text-[#73706A]">Total</p>
            <p className="text-2xl font-semibold text-[#1A1A1A] font-['Outfit']">{total}</p>
          </button>
          <button onClick={() => setStatusFilter('pago')} className={`stat-card text-left transition-all ${statusFilter === 'pago' ? 'ring-2 ring-[#2D6A4F]' : ''}`}>
            <p className="text-sm text-[#73706A]">Pagos</p>
            <p className="text-2xl font-semibold text-[#2D6A4F] font-['Outfit']">{paid}</p>
          </button>
          <button onClick={() => setStatusFilter('pendente')} className={`stat-card text-left transition-all ${statusFilter === 'pendente' ? 'ring-2 ring-[#E85D04]' : ''}`}>
            <p className="text-sm text-[#73706A]">Pendentes</p>
            <p className="text-2xl font-semibold text-[#E85D04] font-['Outfit']">{pending}</p>
          </button>
          <div className="stat-card">
            <p className="text-sm text-[#73706A]">Receita</p>
            <p className="text-2xl font-semibold text-[#2D6A4F] font-['Outfit']">${revenue.toFixed(2)}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1 min-w-[220px]">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#73706A]" />
            <Input placeholder="Buscar nome, email, telefone ou data..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
          </div>
          <div className="flex items-center gap-1 bg-[#F5F2EB] rounded-lg p-1">
            <button onClick={() => setSourceFilter('all')} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sourceFilter === 'all' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`}>Todos</button>
            <button onClick={() => setSourceFilter('portal')} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sourceFilter === 'portal' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`}>Aluno ($40)</button>
            <button onClick={() => setSourceFilter('public')} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sourceFilter === 'public' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`}>Não aluno ($50)</button>
          </div>
          <div className="flex items-center gap-1 bg-[#F5F2EB] rounded-lg p-1">
            <button onClick={() => setSortMode('recent')} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'recent' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`}>Recentes</button>
            <button onClick={() => setSortMode('name-asc')} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'name-asc' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`}>A-Z</button>
            <button onClick={() => setSortMode('name-desc')} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${sortMode === 'name-desc' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#73706A]'}`}>Z-A</button>
          </div>
        </div>

        <div className="bg-white border border-[#E6E2D8] rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center"><div className="w-8 h-8 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto" /></div>
          ) : sorted.length === 0 ? (
            <div className="p-8 text-center text-[#73706A]">Nenhum aluguel encontrado</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F5F2EB]">
                    <th className="table-header">Cliente</th>
                    <th className="table-header">Contato</th>
                    <th className="table-header">Horario</th>
                    <th className="table-header">Origem</th>
                    <th className="table-header">Valor</th>
                    <th className="table-header">Pagamento</th>
                    <th className="table-header text-right">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r, index) => (
                    <tr key={r.id} className="hover:bg-[#F5F2EB]/50 transition-colors" data-testid={`court-rental-row-${index}`}>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <div className="w-9 h-9 bg-[#0077B6] rounded-full flex items-center justify-center"><Volleyball size={16} className="text-white" weight="fill" /></div>
                          <div>
                            <div className="font-medium text-[#1A1A1A]">{r.name}</div>
                            {r.notes && <div className="text-xs text-[#73706A] italic">{r.notes}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="table-cell text-xs">
                        <div className="text-[#1A1A1A]">{r.email || '-'}</div>
                        <div className="text-[#73706A]">{r.phone}</div>
                      </td>
                      <td className="table-cell text-xs">
                        <div className="text-[#1A1A1A] font-medium">{r.date}</div>
                        <div className="text-[#73706A]">{r.start_time} - {r.end_time}</div>
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${r.source === 'portal' ? 'badge-success' : 'badge-info'}`}>{r.source === 'portal' ? 'aluno' : 'publico'}</span>
                      </td>
                      <td className="table-cell font-medium text-[#2D6A4F]">$ {(Number(r.amount) || 0).toFixed(2)}</td>
                      <td className="table-cell">
                        <span className={`badge ${r.payment_status === 'pago' ? 'badge-success' : 'badge-warning'}`}>{r.payment_status || 'pendente'}</span>
                        {r.payment_method && r.payment_method !== '-' && <div className="text-xs text-[#73706A] mt-0.5">({r.payment_method})</div>}
                      </td>
                      <td className="table-cell text-right">
                        <div className="flex items-center justify-end gap-1">
                          {r.payment_status !== 'pago' && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleStatusUpdate(r.id)} className="text-[#2D6A4F] hover:bg-green-50" title="Confirmar pagamento">
                                <CheckCircle size={18} />
                              </Button>
                              <label className="cursor-pointer">
                                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => handleZelleUpload(r.id, e.target.files[0])} />
                                <Button variant="ghost" size="sm" className="text-[#0077B6] hover:bg-blue-50 pointer-events-none" disabled={uploadingZelle === r.id}>
                                  {uploadingZelle === r.id ? <div className="w-4 h-4 border-2 border-[#0077B6] border-t-transparent rounded-full animate-spin" /> : <UploadSimple size={18} />}
                                </Button>
                              </label>
                            </>
                          )}
                          {r.zelle_receipt_path && (
                            <Button variant="ghost" size="sm" onClick={() => viewReceipt(r.id)} className="text-[#0077B6] hover:bg-blue-50" title="Ver comprovante">
                              <ImageIcon size={18} />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)} className="text-[#9B2226] hover:bg-red-50">
                            <Trash size={18} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
          <div className="flex items-center gap-3 mb-3">
            <Phone size={24} className="text-[#0077B6]" />
            <h3 className="font-['Outfit'] font-medium text-lg text-[#1A1A1A]">Pagamento Zelle</h3>
          </div>
          <p className="text-sm text-[#73706A]">Clique no icone <UploadSimple size={14} className="inline text-[#0077B6]" /> para anexar o comprovante Zelle do cliente. O sistema confirma o aluguel automaticamente.</p>
        </div>
      </div>
    </Layout>
  );
}
